import React from 'react';
import { motion } from 'framer-motion';
import { EXPERIENCE } from '../../data/config';
import { MapPin } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};
const item = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE } },
};

const Experience = () => {
  return (
    <section id="experience" className="section-container">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-20 max-w-3xl">
          <p className="text-[0.7rem] uppercase tracking-[0.4em] text-coffee-300 mb-5">Experience</p>
          <h2 className="text-5xl md:text-7xl font-bold mb-6 gradient-text leading-[1.05]">
            Where I've worked.
          </h2>
          <p className="text-lg md:text-xl text-cream-200/80 leading-relaxed">
            Building scalable systems and leading high-performance teams.
          </p>
        </div>

        {/* Entries — editorial list */}
        <motion.ol
          className="divide-y divide-cream-100/8 border-t border-cream-100/8"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
        >
          {EXPERIENCE.map((exp) => (
            <motion.li
              key={exp.id}
              variants={item}
              className="group grid grid-cols-1 md:grid-cols-[140px_1fr] gap-6 md:gap-12 py-12 md:py-14"
            >
              {/* Year column */}
              <div className="md:pt-2">
                <div className="text-xs font-mono uppercase tracking-wider text-coffee-300 whitespace-nowrap">
                  {exp.period}
                </div>
                <div className="hidden md:flex items-center gap-2 mt-3 text-xs text-coffee-400">
                  <MapPin className="w-3 h-3" />
                  {exp.location}
                </div>
              </div>

              {/* Content column */}
              <div>
                {/* Company + role */}
                <div className="flex items-start gap-4 mb-4">
                  {exp.logo && exp.logo.startsWith('http') ? (
                    <img
                      src={exp.logo}
                      alt={`${exp.company} logo`}
                      className="w-10 h-10 object-contain rounded-lg flex-shrink-0 mt-1 opacity-90 group-hover:opacity-100 transition-opacity"
                    />
                  ) : (
                    <span className="text-2xl flex-shrink-0 mt-1 opacity-90 group-hover:opacity-100 transition-opacity">
                      {exp.logo}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl md:text-3xl font-bold text-cream-100 leading-tight mb-1">
                      {exp.company}
                    </h3>
                    <div className="text-base text-caramel-300">
                      {exp.role}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-cream-200/80 text-base md:text-lg leading-relaxed mb-6 max-w-2xl">
                  {exp.description}
                </p>

                {/* Achievements — quiet bulleted list, no check-mark noise */}
                <ul className="space-y-2 mb-6">
                  {exp.achievements.map((achievement, i) => (
                    <li
                      key={i}
                      className="flex gap-3 text-sm text-cream-200/70 leading-relaxed"
                    >
                      <span className="text-caramel-400/60 select-none flex-shrink-0">—</span>
                      <span>{achievement}</span>
                    </li>
                  ))}
                </ul>

                {/* Tech */}
                <div className="flex flex-wrap gap-1.5">
                  {exp.techStack.map((tech, i) => (
                    <span key={i} className="tech-badge">
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Mobile-only location, since left column is hidden */}
                <div className="md:hidden flex items-center gap-2 mt-5 text-xs text-coffee-400">
                  <MapPin className="w-3 h-3" />
                  {exp.location}
                </div>
              </div>
            </motion.li>
          ))}
        </motion.ol>
      </div>
    </section>
  );
};

export default Experience;
